/**
 * Push Notification Utility
 * Handles push notification subscription and management
 */

/**
 * Convert VAPID key from base64 to Uint8Array
 */
const urlBase64ToUint8Array = base64String => {
  // Validate input
  if (!base64String || typeof base64String !== 'string') {
    throw new Error('Invalid VAPID public key: key is empty or not a string')
  }

  // Aggressively clean the key - remove all whitespace, quotes, and control characters
  const cleaned = base64String
    .trim()
    .replace(/['"]/g, '') // Remove quotes
    .replace(/\s+/g, '') // Remove all whitespace
    .replace(/[\r\n\t]/g, '') // Remove newlines, carriage returns, tabs
    .replace(/[^\x20-\x7E]/g, '') // Remove non-printable characters

  if (cleaned.length === 0) {
    throw new Error('Invalid VAPID public key: key is empty after cleaning')
  }

  // Check if key looks like base64 (only contains valid base64 URL-safe characters)
  if (!/^[A-Za-z0-9_-]+$/.test(cleaned)) {
    const preview = cleaned.substring(0, 20) + (cleaned.length > 20 ? '...' : '')
    throw new Error(`Invalid VAPID public key format. Key contains invalid characters. Preview: ${preview}`)
  }

  try {
    // Add padding if needed (URL-safe base64 doesn't always have padding)
    const padding = '='.repeat((4 - (cleaned.length % 4)) % 4)
    
    // Convert URL-safe base64 to standard base64
    const base64 = (cleaned + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/')

    // Decode base64 - atob will throw an error if the string is invalid
    const rawData = window.atob(base64)
    
    if (!rawData || rawData.length === 0) {
      throw new Error('Invalid VAPID public key: decoded data is empty')
    }

    // Convert to Uint8Array
    const outputArray = new Uint8Array(rawData.length)
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i)
    }
    
    return outputArray
  } catch (error) {
    // Provide more specific error message with key preview for debugging
    const preview = cleaned.substring(0, 30) + (cleaned.length > 30 ? '...' : '')
    if (error.message && error.message.includes('Invalid base64')) {
      throw new Error(`Failed to decode VAPID public key: Invalid base64 format. Key preview: ${preview}`)
    }
    throw new Error(`Failed to decode VAPID public key: ${error.message}. Key preview: ${preview}`)
  }
}

/**
 * Check if running in Android WebView
 */
const isAndroidWebView = () => {
  if (typeof window === 'undefined') return false
  
  const userAgent = navigator.userAgent || ''
  const isAndroid = /Android/i.test(userAgent)
  
  // Multiple ways to detect WebView:
  // 1. Contains 'wv' (WebView indicator) - most reliable
  // 2. Contains custom app identifier (BCNIndiaApp) - our app specific
  // 3. Has Android but doesn't have standard Chrome Mobile pattern
  const hasWebViewIndicator = userAgent.includes('wv') || 
                               userAgent.includes('BCNIndiaApp')
  
  // Check if it's NOT a regular Chrome Mobile browser
  // Chrome Mobile has pattern like "Chrome/120.0.0.0 Mobile"
  const isNotChromeMobile = !/Chrome\/[.0-9]* Mobile/i.test(userAgent)
  
  // If it has our app identifier, definitely WebView
  if (userAgent.includes('BCNIndiaApp')) {
    return true
  }
  
  // If it has 'wv' indicator, it's WebView
  if (userAgent.includes('wv')) {
    return true
  }
  
  // Additional check: if it has Android but no Chrome Mobile, might be WebView
  // But be more careful here to avoid false positives
  const likelyWebView = isAndroid && isNotChromeMobile && hasWebViewIndicator
  
  // Log for debugging
  if (typeof window !== 'undefined' && window.console && isAndroid) {
    console.log('[PushNotifications] WebView detection:', {
      userAgent,
      isAndroid,
      hasWebViewIndicator,
      isNotChromeMobile,
      likelyWebView,
      containsWv: userAgent.includes('wv'),
      containsBCNIndiaApp: userAgent.includes('BCNIndiaApp')
    })
  }
  
  return likelyWebView
}

/**
 * Check if push notifications are supported
 * In Android WebView, we need to be more lenient as PushManager might not be available
 * until Service Worker is registered
 */
export const isPushSupported = () => {
  // Basic checks
  const hasServiceWorker = 'serviceWorker' in navigator
  const hasNotification = 'Notification' in window
  const hasPushManager = 'PushManager' in window
  
  // In Android WebView, PushManager is NOT supported (known limitation)
  // Even though Service Worker exists, Web Push API (PushManager) is not available
  const isWebView = isAndroidWebView()
  if (isWebView) {
    // IMPORTANT: Android WebView does NOT support PushManager (known platform limitation)
    // However, we'll allow the icon to show so users can attempt to enable notifications
    // The actual subscription will fail gracefully with a clear error message
    // TODO: Implement native FCM push notifications for Android WebView
    
    if (typeof window !== 'undefined' && window.console) {
      console.log('[PushNotifications] Android WebView detected. Support check:', {
        serviceWorker: hasServiceWorker,
        notification: hasNotification,
        pushManager: hasPushManager,
        userAgent: navigator.userAgent,
        supported: hasServiceWorker, // Show icon if Service Worker is available
        note: 'PushManager may not be available, but we\'ll try during subscription'
      })
      
      if (!hasServiceWorker) {
        console.warn('[PushNotifications] Service Worker not available in WebView')
        return false
      }
      
      // Allow icon to show - we'll handle PushManager check during subscription
      console.log('[PushNotifications] ✅ Service Worker available - icon will show. PushManager check will happen during subscription.')
      return true
    }
    
    // If no console, just check Service Worker
    return hasServiceWorker
  }
  
  // For regular browsers, require all three
  return hasServiceWorker && hasPushManager && hasNotification
}

/**
 * Check if notifications are permitted
 */
export const getNotificationPermission = async () => {
  const isWebView = isAndroidWebView()
  
  // In Android WebView, Notification API might not be in window context
  // but notifications work via Service Worker, so we assume 'default' permission
  if (isWebView && !('Notification' in window)) {
    // Check if Service Worker is available - if yes, notifications will work via SW
    if ('serviceWorker' in navigator) {
      // Return 'default' to allow permission request flow
      // The actual permission will be handled via Service Worker
      return 'default'
    }
    return 'not-supported'
  }
  
  if (!('Notification' in window)) {
    return 'not-supported'
  }

  return Notification.permission
}

/**
 * Request notification permission
 */
export const requestNotificationPermission = async () => {
  const isWebView = isAndroidWebView()
  
  // In Android WebView, Notification API might not be in window context
  // but notifications work via Service Worker
  if (isWebView && !('Notification' in window)) {
    // For WebView, if Service Worker is available, we can proceed
    // The Android permission is already granted (checked in Android code)
    // Service Worker will handle notifications via self.registration.showNotification()
    if ('serviceWorker' in navigator) {
      console.log('[PushNotifications] Notification API not in window, but Service Worker can show notifications')
      // Assume granted since Android permission is already granted
      return true
    }
    throw new Error('Service Worker not available - cannot enable notifications')
  }
  
  if (!('Notification' in window)) {
    throw new Error('Notifications are not supported in this browser')
  }

  const permission = await Notification.requestPermission()
  return permission === 'granted'
}

/**
 * Get VAPID public key from server
 */
const getVapidPublicKey = async () => {
  try {
    // Dynamically import to avoid circular dependencies
    const apiModule = await import('../api')
    
    // Check if pushSubscription API is available
    if (!apiModule.userAdmin || !apiModule.userAdmin.pushSubscription) {
      throw new Error('Push subscription API not available')
    }
    
    const response = await apiModule.userAdmin.pushSubscription.getPublicKey()
    
    if (response.code === 200) {
      return response.payload?.publicKey
    }
    
    throw new Error(response.message || 'Failed to get VAPID public key')
  } catch (error) {
    throw error
  }
}

/**
 * Get FCM token from Android WebView
 */
const getFCMToken = async () => {
  return new Promise((resolve, reject) => {
    console.log('[PushNotifications] === GET FCM TOKEN ===')
    console.log('[PushNotifications] window exists:', typeof window !== 'undefined')
    console.log('[PushNotifications] window.Android exists:', typeof window !== 'undefined' && !!window.Android)
    console.log('[PushNotifications] window.Android.getFCMToken exists:', typeof window !== 'undefined' && window.Android ? !!window.Android.getFCMToken : false)
    
    // Check if Android interface is available
    if (typeof window === 'undefined' || !window.Android || !window.Android.getFCMToken) {
      const error = 'Android FCM interface not available'
      console.error('[PushNotifications] ❌', error)
      console.error('[PushNotifications] Android object:', typeof window !== 'undefined' ? (window.Android ? 'exists' : 'missing') : 'window undefined')
      console.error('[PushNotifications] getFCMToken method:', typeof window !== 'undefined' && window.Android ? (window.Android.getFCMToken ? 'exists' : 'missing') : 'N/A')
      reject(new Error(error))
      return
    }

    try {
      console.log('[PushNotifications] ✅ Android interface available, getting FCM token...')
      
      // Try to get token directly
      const token = window.Android.getFCMToken()
      console.log('[PushNotifications] Direct token call result:', token ? `Token length: ${token.length}` : 'null/empty')
      
      if (token && token.length > 0) {
        console.log('[PushNotifications] ✅ FCM token retrieved directly:', token.substring(0, 30) + '...')
        resolve(token)
        return
      }

      console.log('[PushNotifications] ⏳ FCM token empty from direct call, waiting for token event...')

      // If token is empty, wait for token event
      const timeout = setTimeout(() => {
        window.removeEventListener('fcmTokenReceived', tokenHandler)
        const error = 'FCM token timeout - token not received within 10 seconds'
        if (typeof window !== 'undefined' && window.console) {
          console.error('[PushNotifications]', error)
        }
        reject(new Error(error))
      }, 10000)

      const tokenHandler = (event) => {
        console.log('[PushNotifications] 📨 fcmTokenReceived event received:', event)
        clearTimeout(timeout)
        window.removeEventListener('fcmTokenReceived', tokenHandler)
        if (event.detail && event.detail.token) {
          console.log('[PushNotifications] ✅ FCM token received via event:', event.detail.token.substring(0, 30) + '...')
          resolve(event.detail.token)
        } else {
          const error = 'Invalid FCM token received'
          console.error('[PushNotifications] ❌', error, event)
          reject(new Error(error))
        }
      }

      console.log('[PushNotifications] 📡 Adding event listener for fcmTokenReceived')
      window.addEventListener('fcmTokenReceived', tokenHandler)
    } catch (error) {
      if (typeof window !== 'undefined' && window.console) {
        console.error('[PushNotifications] Error getting FCM token:', error)
      }
      reject(error)
    }
  })
}

/**
 * Subscribe to FCM push notifications (Android WebView)
 */
const subscribeToFCM = async () => {
  try {
    if (typeof window !== 'undefined' && window.console) {
      console.log('[PushNotifications] === SUBSCRIBING TO FCM ===')
    }
    
    // Get FCM token from Android
    const fcmToken = await getFCMToken()
    
    if (!fcmToken || fcmToken.length === 0) {
      throw new Error('FCM token not available')
    }

    if (typeof window !== 'undefined' && window.console) {
      console.log('[PushNotifications] FCM token obtained, sending to backend...')
    }

    // Send FCM token to server
    const apiModule = await import('../api')
    const { getAuthToken } = await import('./authManager')
    
    const token = getAuthToken()
    if (!token) {
      throw new Error('User not authenticated')
    }

    // First, unsubscribe from any existing webpush subscriptions for this user
    // This ensures we replace webpush with FCM in Android WebView
    try {
      const existingSub = JSON.parse(localStorage.getItem('pushSubscription') || '{}')
      if (existingSub.type === 'webpush' && existingSub.endpoint) {
        console.log('[PushNotifications] Found existing webpush subscription, unsubscribing first...')
        try {
          await apiModule.userAdmin.pushSubscription.unsubscribe({
            endpoint: existingSub.endpoint,
            type: 'webpush'
          })
          console.log('[PushNotifications] ✅ Unsubscribed from webpush subscription')
        } catch (unsubError) {
          console.warn('[PushNotifications] ⚠️ Failed to unsubscribe from webpush (may not exist):', unsubError.message)
        }
      }
    } catch (cleanupError) {
      console.warn('[PushNotifications] ⚠️ Error during webpush cleanup:', cleanupError.message)
    }

    const subscriptionData = {
      type: 'fcm',
      fcmToken: fcmToken,
      endpoint: `fcm:${fcmToken}`,
      userAgent: navigator.userAgent
    }

    if (typeof window !== 'undefined' && window.console) {
      console.log('[PushNotifications] Subscription data:', {
        type: subscriptionData.type,
        fcmTokenLength: subscriptionData.fcmToken.length,
        endpoint: subscriptionData.endpoint.substring(0, 30) + '...'
      })
    }

    // Check if pushSubscription API is available
    if (!apiModule.userAdmin || !apiModule.userAdmin.pushSubscription || !apiModule.userAdmin.pushSubscription.subscribe) {
      throw new Error('Push subscription API not available')
    }

    const response = await apiModule.userAdmin.pushSubscription.subscribe(subscriptionData)

    if (typeof window !== 'undefined' && window.console) {
      console.log('[PushNotifications] Backend response:', response)
    }

    if (response.code !== 201 && response.code !== 200) {
      throw new Error(response.message || 'Failed to subscribe to FCM push notifications')
    }

    // Store subscription in localStorage
    localStorage.setItem('pushSubscription', JSON.stringify({
      ...subscriptionData,
      type: 'fcm'
    }))

    if (typeof window !== 'undefined' && window.console) {
      console.log('[PushNotifications] ✅ FCM subscription successful!')
    }

    return { success: true, subscription: subscriptionData }
  } catch (error) {
    if (typeof window !== 'undefined' && window.console) {
      console.error('[PushNotifications] ❌ FCM subscription failed:', error)
    }
    throw error
  }
}

/**
 * Subscribe to push notifications
 */
export const subscribeToPush = async () => {
  try {
    const isWebView = isAndroidWebView()
    
    // For Android WebView, use FCM instead of Web Push
    if (isWebView) {
      if (typeof window !== 'undefined' && window.console) {
        console.log('[PushNotifications] Android WebView detected, using FCM instead of Web Push')
      }
      
      // Check if Android interface is available
      if (typeof window !== 'undefined' && window.Android && window.Android.getFCMToken) {
        if (typeof window !== 'undefined' && window.console) {
          console.log('[PushNotifications] Android FCM interface available, subscribing to FCM...')
        }
        return await subscribeToFCM()
      } else {
        const error = 'Android FCM interface not available. Please ensure you are using the native Android app.'
        if (typeof window !== 'undefined' && window.console) {
          console.error('[PushNotifications]', error)
          console.error('[PushNotifications] Android object:', typeof window.Android)
          console.error('[PushNotifications] getFCMToken method:', window.Android?.getFCMToken ? 'exists' : 'missing')
        }
        throw new Error(error)
      }
    }

    // For regular browsers, use Web Push
    // Check if push is supported
    if (!isPushSupported()) {
      throw new Error('Push notifications are not supported in this browser')
    }

    // Check permission
    let permission = await getNotificationPermission()
    
    if (permission === 'default') {
      const granted = await requestNotificationPermission()
      if (!granted) {
        throw new Error('Notification permission denied')
      }
      permission = 'granted'
    }

    if (permission !== 'granted') {
      throw new Error('Notification permission not granted')
    }

    // Register service worker if not already registered
    let registration
    try {
      registration = await navigator.serviceWorker.ready
    } catch (error) {
      // If service worker isn't ready, try to register it
      if (typeof window !== 'undefined' && window.console) {
        console.log('[PushNotifications] Service Worker not ready, registering...')
      }
      registration = await navigator.serviceWorker.register('/service-worker.js', {
        scope: '/'
      })
      // Wait for it to be ready
      registration = await navigator.serviceWorker.ready
    }

    // Check if PushManager is available
    if (!registration.pushManager && !('PushManager' in window)) {
      // For regular browsers, try a brief wait (might be timing issue)
      if (typeof window !== 'undefined' && window.console) {
        console.log('[PushNotifications] PushManager not immediately available, waiting briefly...')
      }
      
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      // Check again after brief wait
      if (!registration.pushManager && !('PushManager' in window)) {
        const errorMsg = 'PushManager is not available in this browser. Please update your browser or check if Web Push is supported.'
        
        if (typeof window !== 'undefined' && window.console) {
          console.error('[PushNotifications]', errorMsg)
          console.error('[PushNotifications] Service Worker registration:', registration)
          console.error('[PushNotifications] User Agent:', navigator.userAgent)
        }
        
        throw new Error(errorMsg)
      }
    }

    // Get VAPID public key
    const vapidPublicKey = await getVapidPublicKey()
    if (!vapidPublicKey || typeof vapidPublicKey !== 'string' || vapidPublicKey.trim().length === 0) {
      throw new Error('VAPID public key not available or invalid')
    }

    // Convert VAPID key to Uint8Array with validation
    let applicationServerKey
    try {
      applicationServerKey = urlBase64ToUint8Array(vapidPublicKey)
    } catch (error) {
      throw new Error(`Invalid VAPID public key format: ${error.message}`)
    }

    // Subscribe to push
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey
    })

    // Send subscription to server
    const apiModule = await import('../api')
    const { getAuthToken } = await import('./authManager')
    
    const token = getAuthToken()
    if (!token) {
      throw new Error('User not authenticated')
    }

    const subscriptionData = {
      type: 'webpush',
      endpoint: subscription.endpoint,
      keys: {
        p256dh: btoa(
          String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('p256dh')))
        ),
        auth: btoa(
          String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('auth')))
        )
      },
      userAgent: navigator.userAgent
    }

    // Check if pushSubscription API is available
    if (!apiModule.userAdmin || !apiModule.userAdmin.pushSubscription) {
      throw new Error('Push subscription API not available')
    }

    const response = await apiModule.userAdmin.pushSubscription.subscribe(subscriptionData)

    if (response.code !== 201 && response.code !== 200) {
      throw new Error(response.message || 'Failed to subscribe to push notifications')
    }

    // Store subscription in localStorage
    localStorage.setItem('pushSubscription', JSON.stringify(subscriptionData))

    return { success: true, subscription: subscriptionData }
  } catch (error) {
    throw error
  }
}

/**
 * Unsubscribe from push notifications
 */
export const unsubscribeFromPush = async () => {
  try {
    const isWebView = isAndroidWebView()
    const subscriptionData = JSON.parse(localStorage.getItem('pushSubscription') || '{}')
    
    // For FCM (WebView), just remove from server and localStorage
    if (isWebView && subscriptionData.type === 'fcm') {
      const endpoint = subscriptionData.endpoint || `fcm:${subscriptionData.fcmToken}`
      
      // Remove from server
      try {
        const apiModule = await import('../api')
        const { getAuthToken } = await import('./authManager')
        const token = getAuthToken()
        
        if (token && apiModule.userAdmin && apiModule.userAdmin.pushSubscription && apiModule.userAdmin.pushSubscription.unsubscribe) {
          await apiModule.userAdmin.pushSubscription.unsubscribe({
            endpoint: endpoint
          })
        }
      } catch (apiError) {
        // Don't fail the whole unsubscribe if API call fails
      }

      // Remove from localStorage
      localStorage.removeItem('pushSubscription')
      return { success: true }
    }

    // For Web Push (regular browsers)
    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()

      if (subscription) {
        const endpoint = subscriptionData.endpoint || subscription.endpoint

        // Unsubscribe from browser push manager
        await subscription.unsubscribe()

        // Remove from server
        try {
          const apiModule = await import('../api')
          const { getAuthToken } = await import('./authManager')
          const token = getAuthToken()
          
          if (token && apiModule.userAdmin && apiModule.userAdmin.pushSubscription && apiModule.userAdmin.pushSubscription.unsubscribe) {
            await apiModule.userAdmin.pushSubscription.unsubscribe({
              endpoint: endpoint
            })
          }
        } catch (apiError) {
          // Don't fail the whole unsubscribe if API call fails
        }

        // Remove from localStorage
        localStorage.removeItem('pushSubscription')
      } else {
        // No subscription found, but try to clean up localStorage anyway
        localStorage.removeItem('pushSubscription')
      }
    } catch (error) {
      // If service worker or push manager is not available, just clean up localStorage
      localStorage.removeItem('pushSubscription')
    }

    return { success: true }
  } catch (error) {
    throw error
  }
}

/**
 * Check if user is subscribed to push notifications
 */
export const isSubscribed = async () => {
  try {
    const isWebView = isAndroidWebView()
    
    // For FCM (WebView), check localStorage
    if (isWebView) {
      const subscriptionData = JSON.parse(localStorage.getItem('pushSubscription') || '{}')
      if (subscriptionData.type === 'fcm' && subscriptionData.fcmToken) {
        // Also verify token is still available from Android
        if (typeof window !== 'undefined' && window.Android && window.Android.getFCMToken) {
          try {
            const currentToken = window.Android.getFCMToken()
            return currentToken === subscriptionData.fcmToken && currentToken.length > 0
          } catch (error) {
            // If we can't get token, assume subscribed if we have it in localStorage
            return subscriptionData.fcmToken && subscriptionData.fcmToken.length > 0
          }
        }
        return subscriptionData.fcmToken && subscriptionData.fcmToken.length > 0
      }
      return false
    }

    // For Web Push (regular browsers)
    if (!isPushSupported()) {
      return false
    }

    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      return !!subscription
    } catch (error) {
      // If service worker is not available, check localStorage as fallback
      const subscriptionData = JSON.parse(localStorage.getItem('pushSubscription') || '{}')
      return !!(subscriptionData.endpoint && subscriptionData.keys)
    }
  } catch (error) {
    return false
  }
}

/**
 * Initialize push notifications
 * Call this when the app loads and user is authenticated
 */
export const initializePushNotifications = async () => {
  try {
    // Check if push is supported
    const supported = isPushSupported()
    const isWebView = isAndroidWebView()
    
    // Log support check for debugging (especially useful in Android WebView)
    if (typeof window !== 'undefined' && window.console) {
      const supportDetails = {
        serviceWorker: 'serviceWorker' in navigator,
        pushManager: 'PushManager' in window,
        notification: 'Notification' in window,
        userAgent: navigator.userAgent,
        isWebView: isWebView
      }
      console.log('[PushNotifications] Support check:', supportDetails)
      
      if (isWebView && !('Notification' in window)) {
        console.log('[PushNotifications] Note: Notification API not in window, but will be available in Service Worker context')
      }
    }
    
    if (!supported) {
      if (typeof window !== 'undefined' && window.console) {
        console.warn('[PushNotifications] Push notifications not supported in this browser/WebView')
      }
      return { supported: false }
    }

    // Register service worker
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/service-worker.js', {
          scope: '/'
        })
        
        // Wait for service worker to be ready
        await navigator.serviceWorker.ready
        
        // In WebView, check if Notification API becomes available after SW registration
        if (isWebView && !('Notification' in window)) {
          console.log('[PushNotifications] Notification API not in window, but Service Worker can show notifications via self.registration.showNotification()')
        }
      } catch (error) {
        if (typeof window !== 'undefined' && window.console) {
          console.error('[PushNotifications] Service Worker registration failed:', error)
        }
        return { supported: false, error: error.message }
      }
    }

    // Check if already subscribed
    const subscribed = await isSubscribed()
    if (subscribed) {
      return { supported: true, subscribed: true }
    }

    // Check permission
    let permission = await getNotificationPermission()
    
    // If permission is default (not asked yet), request it
    if (permission === 'default') {
      try {
        // In WebView, if Notification API is not available, assume permission is granted
        // since Android permission is already granted and Service Worker can show notifications
        if (isWebView && !('Notification' in window)) {
          console.log('[PushNotifications] WebView: Notification API not in window, but Android permission granted - proceeding')
          permission = 'granted'
        } else {
          const granted = await requestNotificationPermission()
          if (!granted) {
            permission = 'denied'
            return { supported: true, subscribed: false, permission, error: 'Notification permission denied' }
          }
          permission = 'granted'
        }
        
        // Auto-subscribe after permission is granted
        try {
          await subscribeToPush()
          return { supported: true, subscribed: true }
        } catch (error) {
          return { supported: true, subscribed: false, error: error.message }
        }
      } catch (error) {
        return { supported: true, subscribed: false, permission, error: error.message }
      }
    }
    
    if (permission === 'granted') {
      // Auto-subscribe if permission is already granted
      try {
        await subscribeToPush()
        return { supported: true, subscribed: true }
      } catch (error) {
        return { supported: true, subscribed: false, error: error.message }
      }
    }

    return { supported: true, subscribed: false, permission }
  } catch (error) {
    return { supported: false, error: error.message }
  }
}
